import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    CircularProgress,
    Button,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider,
    IconButton,
    TextField,
    Divider,
    DialogProps
} from '@mui/material';
import {
    AccountCircle as AccountCircleIcon,
    ExpandMore as ExpandMoreIcon,
    CardMembership as MembershipIcon,
    QrCode as QrCodeIcon,
    Add as AddIcon,
    CardGiftcard as CardGiftcardIcon,
    PhotoCamera as PhotoCameraIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Edit as EditIcon,
    Cancel as CancelIcon,
    Save as SaveIcon,
    LockReset as LockResetIcon,
    ChangeCircle as ChangeCircleIcon
} from '@mui/icons-material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '../../context/AuthContext';
import { memberProfileAPI, guestPassAPI, userAPI } from '../../services/api';
import { MemberProfile } from '../../types/memberProfile';
import { GuestPass } from '../../types/guestPass';
import { formatDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';
import { GuestPassDialog } from '../../components/guestPass/GuestPassDialog';
import { GuestPassCard } from '../../components/guestPass/GuestPassCard';

const MemberProfilePage: React.FC = () => {
    const { user, memberProfile, isReady } = useRequiredMemberContext();
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
    const [loadingGuestPasses, setLoadingGuestPasses] = useState(false);
    const [guestPassDialogOpen, setGuestPassDialogOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFormData, setEditFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);

    // Password reset state
    const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
    const [passwordResetData, setPasswordResetData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [resettingPassword, setResettingPassword] = useState(false);
    const [passwordResetError, setPasswordResetError] = useState<string | null>(null);

    // Profile picture state
    const [profilePictureBlobUrl, setProfilePictureBlobUrl] = useState<string | null>(null);
    const [profilePictureHovered, setProfilePictureHovered] = useState(false);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // QR code state
    const [qrCodeBlobUrl, setQrCodeBlobUrl] = useState<string | null>(null);
    const [loadingQRCode, setLoadingQRCode] = useState(false);

    // Crop dialog state
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const [zoom, setZoom] = useState<number>(1);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [originalFile, setOriginalFile] = useState<File | null>(null);

    useEffect(() => {
        if (isReady && memberProfile?.id) {
            loadGuestPasses();
            // Initialize edit form data
            setEditFormData({
                firstName: memberProfile.firstName || '',
                lastName: memberProfile.lastName || '',
                email: memberProfile.email || '',
                phone: memberProfile.phone || ''
            });
        }
    }, [isReady, memberProfile?.id]);

    // Fetch profile picture as blob
    useEffect(() => {
        const fetchProfilePicture = async () => {
            if (memberProfile?.profilePictureUrl && memberProfile?.id) {
                try {
                    const response = await memberProfileAPI.getProfilePicture(memberProfile.id);
                    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
                    const blobUrl = URL.createObjectURL(blob);
                    setProfilePictureBlobUrl(blobUrl);
                } catch (err) {
                    console.error('Error fetching profile picture:', err);
                    // If fetch fails, keep null and show placeholder
                }
            } else {
                // Clean up blob URL if no picture
                if (profilePictureBlobUrl) {
                    URL.revokeObjectURL(profilePictureBlobUrl);
                    setProfilePictureBlobUrl(null);
                }
            }
        };

        fetchProfilePicture();

        // Cleanup function to revoke blob URL
        return () => {
            if (profilePictureBlobUrl) {
                URL.revokeObjectURL(profilePictureBlobUrl);
            }
        };
    }, [memberProfile?.profilePictureUrl, memberProfile?.id]);

    // Fetch QR code as blob
    useEffect(() => {
        const fetchQRCode = async () => {
            if (memberProfile?.membershipNumber && memberProfile?.id) {
                setLoadingQRCode(true);
                try {
                    const response = await memberProfileAPI.getMemberProfileQRCode(memberProfile.id);
                    const blob = new Blob([response.data], { type: 'image/png' });
                    const blobUrl = URL.createObjectURL(blob);
                    setQrCodeBlobUrl(blobUrl);
                } catch (err) {
                    console.error('Error fetching QR code:', err);
                    // If fetch fails, keep null and don't show QR code
                } finally {
                    setLoadingQRCode(false);
                }
            } else {
                // Clean up blob URL if no membership number
                if (qrCodeBlobUrl) {
                    URL.revokeObjectURL(qrCodeBlobUrl);
                    setQrCodeBlobUrl(null);
                }
            }
        };

        fetchQRCode();

        // Cleanup function to revoke blob URL
        return () => {
            if (qrCodeBlobUrl) {
                URL.revokeObjectURL(qrCodeBlobUrl);
            }
        };
    }, [memberProfile?.membershipNumber, memberProfile?.id]);

    const loadGuestPasses = async () => {
        if (!memberProfile?.id) return;

        setLoadingGuestPasses(true);
        try {
            const response = await guestPassAPI.getActiveMemberPasses(memberProfile.id);
            setGuestPasses(response.data);
        } catch (err: any) {
            console.error('Error loading guest passes:', err);
        } finally {
            setLoadingGuestPasses(false);
        }
    };

    const handleGuestPassCreated = (newPass: GuestPass) => {
        setGuestPasses([newPass, ...guestPasses]);
        setSnackbarMessage('Guest pass created successfully!');
    };

    const handleCopyCode = (code: string) => {
        setSnackbarMessage(`Code ${code} copied to clipboard!`);
    };

    // Edit profile handlers
    const handleEditClick = () => {
        if (!memberProfile) return;
        setEditFormData({
            firstName: memberProfile.firstName || '',
            lastName: memberProfile.lastName || '',
            email: memberProfile.email || '',
            phone: memberProfile.phone || ''
        });
        setEmailConfirmation('');
        setIsEditMode(true);

        // Scroll to top of the page to show the profile section
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEmailConfirmation('');
        setError(null);
    };

    const handleSaveProfile = async () => {
        if (!memberProfile?.id) return;

        // Validation
        if (!editFormData.firstName.trim()) {
            setError('First name is required');
            return;
        }
        if (!editFormData.lastName.trim()) {
            setError('Last name is required');
            return;
        }
        if (!editFormData.email.trim()) {
            setError('Email is required');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editFormData.email)) {
            setError('Invalid email format');
            return;
        }

        // If email changed, require confirmation
        if (editFormData.email !== memberProfile.email) {
            if (!emailConfirmation) {
                setError('Please confirm your new email address');
                return;
            }
            if (editFormData.email !== emailConfirmation) {
                setError('Email and confirmation do not match');
                return;
            }
        }

        setSavingProfile(true);
        setError(null);

        try {
            const emailChanged = editFormData.email !== memberProfile.email;

            const response = await memberProfileAPI.updateMemberProfileBasicInfo(memberProfile.id, {
                ...editFormData,
                emailConfirmation: emailChanged ? emailConfirmation : undefined
            });

            // If email was changed, log the user out and redirect to login
            if (emailChanged && response.data.emailUpdatedAcrossProfiles) {
                // Show message that they need to log back in
                setSnackbarMessage(`Email updated successfully across ${response.data.updatedProfilesCount} profile(s). You will be logged out to refresh your session. Please log in with your new email address.`);

                // Wait for snackbar to be visible, then log out
                setTimeout(async () => {
                    // Clear all local storage and cookies
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('tenantId');
                    localStorage.removeItem('selectedMemberProfile');

                    // Call logout function to clean up auth context
                    if (logout) {
                        await logout();
                    }

                    // Redirect to login page
                    navigate('/login', {
                        state: {
                            message: 'Your email has been updated. Please log in with your new email address.'
                        }
                    });
                }, 3000);

                return;
            }

            // Show success message for non-email changes
            setSnackbarMessage('Profile updated successfully!');

            // Exit edit mode
            setIsEditMode(false);
            setEmailConfirmation('');

            // Reload the page to reflect changes
            window.location.reload();
        } catch (err: any) {
            console.error('Error updating profile:', err);
            setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setSavingProfile(false);
        }
    };

    // Placeholder handlers
    const handleResetPassword = () => {
        setPasswordResetDialogOpen(true);
        setPasswordResetData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordResetError(null);
    };

    const handleCancelPasswordReset = (event?: any, reason?: string) => {
        if (reason && reason === "backdropClick") {
            return;
        }
        setPasswordResetDialogOpen(false);
        setPasswordResetData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordResetError(null);
    };

    const handleSavePassword = async () => {
        if (!user?.id) return;

        // Clear any previous errors
        setPasswordResetError(null);

        // Validation
        if (!passwordResetData.currentPassword) {
            setPasswordResetError('Current password is required');
            return;
        }
        if (!passwordResetData.newPassword) {
            setPasswordResetError('New password is required');
            return;
        }
        if (passwordResetData.newPassword.length < 8) {
            setPasswordResetError('New password must be at least 8 characters long');
            return;
        }
        if (!passwordResetData.confirmPassword) {
            setPasswordResetError('Please confirm your new password');
            return;
        }
        if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
            setPasswordResetError('New passwords do not match');
            return;
        }

        setResettingPassword(true);

        try {
            await userAPI.resetPassword(user.id, {
                currentPassword: passwordResetData.currentPassword,
                newPassword: passwordResetData.newPassword
            });

            setSnackbarMessage('Password reset successfully!');
            handleCancelPasswordReset();
        } catch (err: any) {
            console.error('Error resetting password:', err);
            setPasswordResetError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setResettingPassword(false);
        }
    };

    const handleChangeMembership = () => {
        if (memberProfile?.id) {
            // navigate(`/member-profiles/${memberProfile.id}/change-membership`);
            navigate('/coming-soon');
        }
    };

    // Profile picture handlers
    const handleProfilePictureClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Only JPEG, JPG, and PNG files are allowed.');
            return;
        }

        // Validate file size (2MB max)
        const maxSize = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > maxSize) {
            setError('File size too large. Maximum allowed size is 2MB.');
            return;
        }

        // Store the original file
        setOriginalFile(file);

        // Read the file and open crop dialog
        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setCropDialogOpen(true);
            // Reset crop and zoom
            setCrop({
                unit: '%',
                width: 90,
                height: 90,
                x: 5,
                y: 5
            });
            setZoom(1);
        };
        reader.readAsDataURL(file);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCropDialogClose = () => {
        setCropDialogOpen(false);
        setImageSrc(null);
        setOriginalFile(null);
        setCompletedCrop(null);
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        
        // Calculate the initial pixel crop based on the percentage crop
        const pixelCrop: PixelCrop = {
            unit: 'px',
            x: (crop.x / 100) * width,
            y: (crop.y / 100) * height,
            width: (crop.width / 100) * width,
            height: (crop.height / 100) * height
        };
        
        setCompletedCrop(pixelCrop);
    };

    const getCroppedImg = async (): Promise<Blob | null> => {
        if (!imgRef.current || !completedCrop) return null;

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return null;

        // Calculate the scaling factor between display size and natural size
        const scaleX = image.naturalWidth / (image.width * zoom);
        const scaleY = image.naturalHeight / (image.height * zoom);

        // Set canvas size to desired output size (fixed at 400x400 for profile pictures)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;

        // Calculate source dimensions from the crop area
        const sourceX = completedCrop.x * scaleX;
        const sourceY = completedCrop.y * scaleY;
        const sourceWidth = completedCrop.width * scaleX;
        const sourceHeight = completedCrop.height * scaleY;

        // Draw the cropped and scaled image
        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            outputSize,
            outputSize
        );

        // Convert canvas to blob
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, originalFile?.type || 'image/jpeg', 0.95);
        });
    };

    const handleCropComplete = async () => {
        if (!memberProfile?.id || !originalFile) return;

        setUploadingPicture(true);
        setError(null);

        try {
            // Get the cropped image blob
            const croppedBlob = await getCroppedImg();
            if (!croppedBlob) {
                throw new Error('Failed to crop image');
            }

            // Convert blob to file
            const croppedFile = new File(
                [croppedBlob],
                originalFile.name,
                { type: originalFile.type }
            );

            const response = await memberProfileAPI.uploadProfilePicture(memberProfile.id, croppedFile);
            setSnackbarMessage('Profile picture uploaded successfully!');

            // Fetch the new profile picture as blob
            try {
                const pictureResponse = await memberProfileAPI.getProfilePicture(memberProfile.id);
                const blob = new Blob([pictureResponse.data], { type: pictureResponse.headers['content-type'] || 'image/jpeg' });

                // Revoke old blob URL if exists
                if (profilePictureBlobUrl) {
                    URL.revokeObjectURL(profilePictureBlobUrl);
                }

                const blobUrl = URL.createObjectURL(blob);
                setProfilePictureBlobUrl(blobUrl);
            } catch (blobErr) {
                console.error('Error fetching uploaded picture:', blobErr);
            }

            // Close the crop dialog
            handleCropDialogClose();
        } catch (err: any) {
            console.error('Error uploading profile picture:', err);
            setError(err.response?.data?.message || 'Failed to upload profile picture. Please try again.');
        } finally {
            setUploadingPicture(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return 'success';
            case 'expired':
            case 'cancelled':
                return 'error';
            case 'suspended':
            case 'on_hold':
                return 'warning';
            case 'pending':
                return 'info';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">User information not available</Alert>
            </Container>
        );
    }

    if (!isReady || !memberProfile) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="info">Loading member profile...</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                Member Profile
            </Typography>

            <Grid container spacing={3}>
                {/* Member Information Card */}
                <Grid size={{ xs: 12 }}>
                    <Card style={{ padding: "16px" }}>
                        <CardHeader
                            avatar={
                                <>
                                    <Box
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundImage: profilePictureBlobUrl ? `url(${profilePictureBlobUrl})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            color: 'white',
                                            fontSize: '2.5rem',
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            '&:hover .edit-overlay': {
                                                opacity: 1
                                            }
                                        }}
                                        onClick={handleProfilePictureClick}
                                        onMouseEnter={() => setProfilePictureHovered(true)}
                                        onMouseLeave={() => setProfilePictureHovered(false)}
                                    >
                                        {/* Show initials as fallback when no profile image */}
                                        {!profilePictureBlobUrl && (
                                            <>{memberProfile.firstName?.charAt(0)}{memberProfile.lastName?.charAt(0)}</>
                                        )}

                                        {/* Edit Overlay */}
                                        <Box
                                            className="edit-overlay"
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: 0,
                                                transition: 'opacity 0.3s',
                                            }}
                                        >
                                            {uploadingPicture ? (
                                                <CircularProgress size={24} sx={{ color: 'white' }} />
                                            ) : (
                                                <PhotoCameraIcon sx={{ fontSize: 32, color: 'white' }} />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Hidden file input */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png"
                                        style={{ display: 'none' }}
                                        onChange={handleFileSelect}
                                    />
                                </>
                            }
                            title={
                                <Box>
                                    <Typography variant="h5" component="div">
                                        {memberProfile.firstName} {memberProfile.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Membership # {memberProfile.membershipNumber || 'N/A'}
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(memberProfile.status)}
                                        color={getStatusColor(memberProfile.status) as any}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>
                            }
                        />
                        <CardContent>
                            {isEditMode ? (
                                <Box sx={{ p: 2 }}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="First Name"
                                                value={editFormData.firstName}
                                                onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                                                required
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Last Name"
                                                value={editFormData.lastName}
                                                onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                                                required
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                type="email"
                                                value={editFormData.email}
                                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                                required
                                                variant="outlined"
                                                helperText={editFormData.email !== memberProfile.email ? "Changing your email will update all your member profiles" : ""}
                                            />
                                        </Grid>
                                        {editFormData.email !== memberProfile.email && (
                                            <Grid size={{ xs: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Confirm New Email"
                                                    type="email"
                                                    value={emailConfirmation}
                                                    onChange={(e) => setEmailConfirmation(e.target.value)}
                                                    onPaste={(e) => e.preventDefault()}
                                                    onCopy={(e) => e.preventDefault()}
                                                    onCut={(e) => e.preventDefault()}
                                                    required
                                                    variant="outlined"
                                                    helperText="Please retype your email to confirm (copy/paste disabled)"
                                                    error={emailConfirmation !== '' && editFormData.email !== emailConfirmation}
                                                />
                                            </Grid>
                                        )}
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                label="Phone"
                                                value={editFormData.phone}
                                                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        {editFormData.email !== memberProfile.email && (
                                            <Grid size={{ xs: 12 }}>
                                                <Alert severity="warning">
                                                    <strong>Important:</strong>
                                                    <p><strong>Changing your email will log you out.</strong></p>
                                                    <p>Also note, all future communications will be sent to the new email effective immediately.
                                                        This change will apply to all member profiles associated with your account.</p>
                                                </Alert>
                                            </Grid>
                                        )}
                                        <Grid size={{ xs: 12 }}>
                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CancelIcon />}
                                                    onClick={handleCancelEdit}
                                                    disabled={savingProfile}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    startIcon={savingProfile ? <CircularProgress size={20} /> : <SaveIcon />}
                                                    onClick={handleSaveProfile}
                                                    disabled={savingProfile}
                                                >
                                                    {savingProfile ? 'Saving...' : 'Save'}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        {loadingQRCode ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : qrCodeBlobUrl ? (
                                            <Box
                                                component="img"
                                                src={qrCodeBlobUrl}
                                                alt="Membership QR Code"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 220,
                                                    height: 'auto',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    padding: 1,
                                                    backgroundColor: '#fff'
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    QR code not available
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 8 }}>

                                        <Grid size={{ xs: 12, sm: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Typography
                                                variant="body1" style={{ overflow: "unset", overflowWrap: "break-word" }} gutterBottom>
                                                {memberProfile.email}
                                            </Typography>
                                        </Grid>
                                        {memberProfile.phone && (
                                            <Grid size={{ xs: 12, sm: 12 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Phone
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {memberProfile.phone}
                                                </Typography>
                                            </Grid>
                                        )}

                                        <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Membership Tier
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {memberProfile.membershipTier?.name || 'Not Assigned'}
                                            </Typography>
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Member Since
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {formatDate(memberProfile.startDate)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Guest Passes Section */}
                <Grid size={{ xs: 12 }}>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="guest-passes-content"
                            id="guest-passes-header"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CardGiftcardIcon color="primary" />
                                <Typography variant="h6">
                                    Guest Passes
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                {memberProfile?.status !== 'active' && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        You must have an active membership to generate guest passes.
                                    </Alert>
                                )}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: 2,
                                    mb: 2
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Active guest passes for your sponsored guests
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setGuestPassDialogOpen(true)}
                                        disabled={memberProfile?.status !== 'active'}
                                        size="small"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Generate Pass
                                    </Button>
                                </Box>

                                {loadingGuestPasses ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : guestPasses.length === 0 ? (
                                    <Alert severity="info">
                                        No active guest passes. Click "Generate Pass" to create one for your guest.
                                    </Alert>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {guestPasses.map((pass) => (
                                            <GuestPassCard
                                                key={pass.id}
                                                guestPass={pass}
                                                onCopyCode={handleCopyCode}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* Account Actions Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Account Actions
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<LockResetIcon />}
                                onClick={handleResetPassword}
                                fullWidth
                            >
                                Reset Password
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={handleEditClick}
                                fullWidth
                            >
                                Edit Profile
                            </Button>
                            {/* Disabling this button for now upon client request */}
                            {/* <Button
                                variant="outlined"
                                startIcon={<ChangeCircleIcon />}
                                onClick={handleChangeMembership}
                                fullWidth
                            >
                                Change Membership
                            </Button> */}
                        </Box>
                    </Paper>
                </Grid>

                {/* Membership Details Section */}
                {/* <Grid size={{ xs: 12 }}>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="membership-benefits-content"
                            id="membership-benefits-header"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MembershipIcon color="primary" />
                                <Typography variant="h6">
                                    Membership Details
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                                <Typography variant="body1" color="text.secondary" align="center">
                                    Membership benefits goes here
                                </Typography>
                                // TODO: This will be expanded with actual membership benefits, features, and detailed information 
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                </Grid> */}
            </Grid>

            {/* Guest Pass Dialog */}
            <GuestPassDialog
                open={guestPassDialogOpen}
                onClose={() => setGuestPassDialogOpen(false)}
                onSuccess={handleGuestPassCreated}
                memberProfileId={memberProfile?.id}
                isAdminMode={false}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
            />

            {/* Image Crop Dialog */}
            <Dialog
                open={cropDialogOpen}
                onClose={handleCropDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Crop Profile Picture
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {/* Crop Area */}
                        {imageSrc && (
                            <Box sx={{
                                maxHeight: '60vh',
                                overflow: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                p: 2
                            }}>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                    aspect={1}
                                    circularCrop
                                >
                                    <img
                                        ref={imgRef}
                                        src={imageSrc}
                                        alt="Crop preview"
                                        onLoad={handleImageLoad}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '50vh',
                                            transform: `scale(${zoom})`,
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </ReactCrop>
                            </Box>
                        )}

                        {/* Zoom Control */}
                        <Box sx={{ px: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ZoomOutIcon />
                                <Slider
                                    value={zoom}
                                    onChange={(_, value) => setZoom(value as number)}
                                    min={0.5}
                                    max={3}
                                    step={0.1}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                    sx={{ flex: 1 }}
                                />
                                <ZoomInIcon />
                            </Box>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                            Drag the crop area to reposition. Use the slider to zoom in/out. On touch devices, you can also pinch to zoom.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCropDialogClose} disabled={uploadingPicture}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCropComplete}
                        variant="contained"
                        color="primary"
                        disabled={uploadingPicture || !completedCrop}
                    >
                        {uploadingPicture ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Reset Dialog */}
            <Dialog
                open={passwordResetDialogOpen}
                disableEscapeKeyDown={true}

                onClose={handleCancelPasswordReset}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Reset Password
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        {passwordResetError && (
                            <Alert severity="error" onClose={() => setPasswordResetError(null)}>
                                {passwordResetError}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Current Password"
                            type="password"
                            value={passwordResetData.currentPassword}
                            onChange={(e) => setPasswordResetData({ ...passwordResetData, currentPassword: e.target.value })}
                            required
                            variant="outlined"
                            disabled={resettingPassword}
                        />

                        <TextField
                            fullWidth
                            label="New Password"
                            type="password"
                            value={passwordResetData.newPassword}
                            onChange={(e) => setPasswordResetData({ ...passwordResetData, newPassword: e.target.value })}
                            required
                            variant="outlined"
                            disabled={resettingPassword}
                            helperText="Must be at least 8 characters long"
                        />

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            value={passwordResetData.confirmPassword}
                            onChange={(e) => setPasswordResetData({ ...passwordResetData, confirmPassword: e.target.value })}
                            onPaste={(e) => e.preventDefault()}
                            onCopy={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            required
                            variant="outlined"
                            disabled={resettingPassword}
                            helperText="Please retype your new password to confirm (copy/paste disabled)"
                            error={passwordResetData.confirmPassword !== '' && passwordResetData.newPassword !== passwordResetData.confirmPassword}
                        />

                        <Alert severity="info">
                            <strong>Password Requirements:</strong>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                <li>At least 8 characters long</li>
                                <li>Must match in both fields</li>
                            </ul>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCancelPasswordReset()} disabled={resettingPassword}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSavePassword}
                        variant="contained"
                        color="primary"
                        disabled={resettingPassword}
                        startIcon={resettingPassword ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {resettingPassword ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default MemberProfilePage;
